/**
 * Help Center: "rewards" section articles. Format and rules: src/content/help/README.md.
 */
export default [
  {
    slug: 'how-miles-work',
    title: 'How Defera Miles work',
    summary: 'You earn 2 Miles for every $1 you pay on completed trips. Use them to lower the price of your next trip, or turn them into an e-gift card.',
    keywords: ['rewards program', 'loyalty points', 'points', 'miles balance', 'redeem', 'perks'],
    related: ['rewards/earning-miles', 'rewards/using-miles-at-checkout', 'rewards/gift-cards', 'rewards/defera-status'],
    updated: '2026-10-05',
    body: `Defera Miles are our rewards program. You earn them on the trips you complete with us, and you can use them to lower the price of your next trip or turn them into an e-gift card.

If you've never used a rewards program before, here's the idea: the more you book with us, the more Miles you collect, and you decide when and how to use them. This page walks you through earning them, using them and keeping track of them.

## How do I earn Miles?

You earn 2 Miles for every $1 of the final amount you pay for a trip. Higher Defera Status tiers earn even more.

Your Miles are added once the trip is completed. A trip is completed when it's paid in full and its confirmation number is released. Your confirmation number is the code the airline uses to find your reservation, and our team releases it after your final payment.

Two things to know:

- **Only completed trips earn Miles.** A cancelled trip doesn't earn any.
- **Miles are earned on money, not Miles.** If you use Miles to pay for part of a trip, that part doesn't earn new Miles.

## What are my Miles worth?

A Mile's value depends on how you use it:

- **On a Defera trip:** each Mile is worth {{MILES_TRIP_VALUE}}.
- **As an e-gift card:** each Mile is worth {{MILES_GIFT_CARD_VALUE}}.

So your Miles go furthest on your next trip. If you'd rather have something now, an e-gift card turns them into value right away.

## What does that look like?

Here's how it adds up, as an example. Say the final amount you pay for a trip is $500. Once that trip is completed, it earns 1,000 Miles: 2 for every $1, or more with a higher Status tier.

On your next trip, those 1,000 Miles would take $12.50 off the price at checkout. As an e-gift card, the same 1,000 Miles would be worth $10.

## How can I use my Miles?

There are four ways:

- **Toward your next trip.** At checkout, your Miles balance appears right above where you add your card. Tap **Apply to trip** or **Choose an amount**. You need at least {{MILES_CHECKOUT_MIN}} Miles. The price drops before your payment plan is built, so your deposit and every payment get smaller.
- **For a whole trip.** If your balance covers a trip's full total, tap **Book with Miles** on the review page at checkout. There's no card and no payment plan.
- **On extras for a trip that isn't completed yet.** The "Redeem on this trip" panel on your trip page has three tiles: **Buy Seats** from {{MILES_SEATS_TILE}} Miles, **Add Baggage** from {{MILES_BAGS_TILE}} Miles and **Cabin Upgrade** from {{MILES_UPGRADE_TILE}} Miles. Each one opens a chat with the Defera Concierge, our AI concierge, which sends your request to our team.
- **As an e-gift card.** Tap **Redeem for E-Gift Card** on the **Rewards** page, or ask the Defera Concierge in chat. You need at least 500 Miles, and you can choose from more than 200 merchants.

Here's [how to use your Miles at checkout](/help/rewards/using-miles-at-checkout), step by step.

## Can I use a trip's Miles on that same trip?

No. The Miles a trip earns go toward your next trip, not the one that earned them. A trip earns its Miles when it's completed, and by then it's already paid off.

That's also why the "Redeem on this trip" tiles turn off once a trip is completed. On a completed trip, the panel's heading changes to "Save for future trips".

## Where can I see my Miles?

Open [Rewards](/rewards) from the main menu. You'll find your Miles balance, your lifetime stats, the Miles Value Calculator and the full program guide.

## Can I earn Miles faster?

Yes, with [Defera Status](/help/rewards/defera-status), our tier program. Your tier depends on your qualifying spend on completed trips in a calendar year. Higher tiers earn more Miles and unlock perks. The tiers are Member, Silver, Gold, Platinum and Diamond.

## What if I cancel a trip?

If you cancel a trip that used Miles, those Miles come back to your account automatically. A cancelled trip doesn't earn new Miles, because only completed trips do. Here's more on [what happens to Miles when you cancel](/help/rewards/miles-when-you-cancel).`,
  },
  {
    slug: 'earning-miles',
    title: 'Earning Defera Miles',
    summary: 'You earn 2 Miles for every $1 you pay for a trip, added once the trip is completed. Higher Status tiers earn more, and cancelled trips earn none.',
    keywords: ['earn rate', 'points per dollar', 'when do miles post', 'miles not showing', 'missing miles', 'bonus miles'],
    related: ['rewards/how-miles-work', 'rewards/defera-status', 'rewards/miles-when-you-cancel', 'emails/paid-off-email'],
    updated: '2026-10-05',
    body: `You earn 2 Defera Miles for every $1 of the final amount you pay for a trip. Your Miles are added once the trip is completed, which means it's paid in full and its confirmation number has been released.

You'll know when they've arrived: the email we send when your trip is paid off tells you how many Miles it earned. And if you have a higher Defera Status tier, you earn even more.

## How much will I earn?

- **2 Miles per $1.** You earn them on the final amount you pay for the trip.
- **More with a higher tier.** Higher Defera Status tiers multiply the Miles you earn. You'll find each tier's multiplier in the program guide on the **Rewards** page.
- **Money, not Miles.** Miles are earned on what you pay in money, not on any part of the trip your Miles covered.

For example, if the final amount you pay for a trip is $500, that trip earns 1,000 Miles once it's completed. A higher Status tier would earn more.

> [!NOTE] There are also birthday bonuses. [Defera Status tiers](/help/rewards/defera-status) explains how tiers and their benefits work.

## When will my Miles arrive?

Your Miles arrive when your trip is completed. Here's what happens:

1. **You make your final payment.** Your trip is now paid in full, and a request to release your confirmation number goes to our team automatically.
2. **Our team releases your confirmation number.** That's the code the airline uses to find your reservation.
3. **Your trip is completed and earns its Miles.** We email you that your trip has been paid off, and the email tells you how many Miles it earned.

How long step 2 takes depends on when your final payment goes through:

- **Monday through Thursday, or Friday morning (Pacific time):** within 24 hours.
- **Friday afternoon or over the weekend:** on Monday, or Tuesday at the latest.

## Can I get my Miles sooner?

Yes. Your Miles arrive once your trip is completed, so paying off your trip early gets them to you sooner. There's no penalty for paying early. Open your trip in **Trips**, tap **Make a Payment** and choose **Pay off**.

## Which trip can I use them on?

The Miles a trip earns go toward your next trip, not the one that earned them. By the time a trip earns its Miles, it's already paid off.

You can use them at checkout on a future trip, or turn them into an e-gift card on the **Rewards** page.

## Which trips don't earn Miles?

- **Cancelled trips.** A cancelled trip earns zero Miles, however the cancellation is settled and whichever Defera plan you were on. If you book another trip later, that trip earns Miles on its own final amount paid once it's completed.
- **Trips booked with Miles alone.** Miles are earned on the money you pay, so a trip paid entirely with Miles doesn't earn new Miles.

## What if my Miles are missing?

Your balance and lifetime stats are on the **Rewards** page. If a trip's Miles aren't there, first check that the trip is completed. Its confirmation number should be on your trip page, and you should have the email saying your trip has been paid off. Miles aren't added before then.

If a completed trip's Miles still aren't in your balance, chat with the [Defera Concierge](/support), our AI concierge, and tell it which trip. It can send a request to our team. The answer will appear on your trip page and arrive by email.`,
  },
  {
    slug: 'using-miles-at-checkout',
    title: 'Using your Miles at checkout',
    summary: 'At checkout, your Miles appear right above your card. Tap Apply to trip or Choose an amount (at least {{MILES_CHECKOUT_MIN}} Miles), and every payment gets smaller.',
    keywords: ['pay with miles', 'redeem miles', 'apply miles', 'miles discount', 'lower deposit', 'points at checkout', 'use points'],
    related: ['rewards/book-with-miles', 'rewards/miles-value', 'booking/checkout-page', 'booking/using-credit-at-checkout'],
    updated: '2026-10-05',
    body: `You can use your Defera Miles to lower the price of a new trip, right at checkout. Your Miles balance appears like a payment method, right above where you add your card, and you choose how many to use.

Nothing comes off unless you choose it. Each Mile you use takes {{MILES_TRIP_VALUE}} off the price. Because the price drops before your payment plan is built, your deposit (if your plan has one) and every payment get smaller.

## How do I use them?

Here's what happens:

1. **Choose your trip.** Search and pick your flights as usual, then continue to checkout.
2. **Find your Miles.** Your Defera Miles balance sits right above where you add your card.
3. **Choose how many to use.** Tap **Apply to trip** to use as many Miles as the trip allows, which isn't always your whole balance. Or tap **Choose an amount** to type a number or set it with the slider.
4. **Your price drops.** The Miles come off before your payment plan is built, so your deposit and every payment shrink.
5. **Confirm your booking.** Your Miles are set aside the moment you confirm, before your card is charged.

Changed your mind before confirming? Tap **Remove** to take the Miles off.

## What does that look like?

For example, if you apply 1,000 Miles, $12.50 comes off your trip's price. Your payment plan is then built on the lower price, so your deposit and each payment are smaller than they would have been.

## What are the rules?

- **Which plans:** Defera Installments, including Flight + Hotel packages, and Defera Zero.
- **A minimum of {{MILES_CHECKOUT_MIN}} Miles.** You need at least that many to use them at checkout.
- **At least {{MILES_MIN_PLAN}} stays on your payment plan** when your Miles cover only part of a trip.
- **Enough for the whole trip?** If your balance covers the trip's full total, you can [book it with Miles alone](/help/rewards/book-with-miles), with no card and no payment plan.
- **Packages can't be booked with Miles alone.** Flight + Hotel packages can use Miles, but part of a package always stays on your payment plan.
- **New Miles are earned on money, not Miles.** The part your Miles covered doesn't earn new Miles.

## When do my Miles leave my balance?

Your Miles are set aside the moment you confirm your booking, before your card is charged.

- **If the payment doesn't go through,** they're given straight back.
- **If your bank asked you to approve the payment and that step wasn't finished,** your Miles can stay set aside until our team releases them.

Some banks ask you to approve online payments, for example with a code sent to your phone. If that happened and your booking didn't go through, chat with the [Defera Concierge](/support), our AI concierge. It can send a request to our team to release your Miles.

## What if I cancel later?

If a trip that used Miles is cancelled, those Miles come back to your account automatically. Here's more on [what happens to Miles when you cancel](/help/rewards/miles-when-you-cancel).`,
  },
  {
    slug: 'book-with-miles',
    title: 'Booking a trip with Miles alone',
    summary: 'When your Miles cover a trip’s full total, tap Book with Miles at checkout: no card, no payment plan. Our team then releases your confirmation number.',
    keywords: ['free flight', 'pay with points', 'award booking', 'miles only', 'no card', 'redeem miles for a flight'],
    related: ['rewards/using-miles-at-checkout', 'rewards/miles-when-you-cancel', 'confirmation-and-travel/when-confirmation-number'],
    updated: '2026-10-05',
    body: `If your Defera Miles balance covers a trip's full total, you can book the whole trip with Miles. Look for **Book with Miles** on the review page at checkout. There's no card to add and no payment plan.

Because there's nothing left to pay, our team releases your confirmation number the same way it does for any trip that's paid in full. Your confirmation number is the code the airline uses to find your reservation.

## How does it work?

Here's what happens:

1. **Choose your trip.** Search and pick your flights, then continue to checkout as usual.
2. **Tap Book with Miles.** You'll see it on the review page when your Miles balance covers the trip's full total.
3. **Your trip is booked.** It's paid with Miles alone, so there's no card to charge and no payment schedule.
4. **Our team releases your confirmation number.** It appears on your trip page in **Trips**.

To see how long the release takes, read [When do I get my confirmation number?](/help/confirmation-and-travel/when-confirmation-number)

## How many Miles do I need?

Enough to cover the trip's full total, at {{MILES_TRIP_VALUE}} a Mile. To see what your balance is worth, use the Miles Value Calculator on the **Rewards** page.

If your Miles cover only part of a trip, you can still [use them at checkout](/help/rewards/using-miles-at-checkout) to lower the price. In that case, at least {{MILES_MIN_PLAN}} stays on your payment plan.

## When isn't it offered?

**Book with Miles** isn't offered on:

- **Flight + Hotel packages.** You can use Miles on a package, but it can't be booked with Miles alone, so part of it always stays on the payment plan.
- **Trips with an airline travel credit applied.** That's a credit from an airline that you add at checkout.

If your balance covers the whole trip and you don't see **Book with Miles**, ask the [Defera Concierge](/support), our AI concierge.

## Do I earn Miles on this trip?

No. Miles are earned on what you pay in money, not on the part your Miles covered. A trip booked with Miles alone doesn't earn new Miles.

## What if I need to cancel?

A trip paid entirely with Miles is cancelled through our team. Here's how:

1. **Open the trip.** Go to **Trips** and tap the trip.
2. **Open Flight Options and tap Cancel.** A chat with the Defera Concierge opens, and it sends your request to our team.
3. **Your Miles come back.** When the trip is cancelled, your Miles return to your account automatically.`,
  },
  {
    slug: 'miles-value',
    title: 'What are my Miles worth?',
    summary: 'Each Mile is worth {{MILES_TRIP_VALUE}} on a Defera trip and {{MILES_GIFT_CARD_VALUE}} as an e-gift card, so your Miles go furthest when you use them on your next trip.',
    keywords: ['mile value', 'cents per mile', 'points value', 'how much are miles worth', 'miles calculator', 'dollar value'],
    related: ['rewards/using-miles-at-checkout', 'rewards/gift-cards', 'rewards/how-miles-work'],
    updated: '2026-10-05',
    body: `Each Defera Mile is worth {{MILES_TRIP_VALUE}} when you use it on a Defera trip, and {{MILES_GIFT_CARD_VALUE}} when you turn it into an e-gift card. That means your Miles go furthest on your next trip.

Neither choice is wrong. If another trip is on your mind, saving your Miles for it gets you the most from them. If you'd rather have something now, an e-gift card turns them into value right away.

## How do the options compare?

Here's what each Mile is worth, depending on how you use it:

| How you use your Miles | Each Mile is worth |
|---|---|
| Toward a Defera trip at checkout | {{MILES_TRIP_VALUE}} |
| Booking a whole trip with Miles | {{MILES_TRIP_VALUE}} |
| An e-gift card from the Rewards page | {{MILES_GIFT_CARD_VALUE}} |

In short, every Mile is worth more on a trip than as a gift card.

## What does that look like?

For example, 1,000 Miles would take $12.50 off the price of a Defera trip at checkout. Turned into an e-gift card, the same 1,000 Miles would be worth $10.

To see what your own balance is worth, open the Miles Value Calculator on the [Rewards](/rewards) page.

## Which should I choose?

- **Planning another trip?** Save your Miles for checkout. Each one is worth the most there, and your deposit and payments get smaller. Here's [how to use your Miles at checkout](/help/rewards/using-miles-at-checkout).
- **Want something now?** Redeem them for an e-gift card at more than 200 merchants, once you have 500 Miles or more. The Rewards page has a gift-card value calculator too.

## Can I use Miles on seats, bags or upgrades?

Yes, on a trip that isn't completed yet. The "Redeem on this trip" panel on your trip page has three tiles: **Buy Seats** from {{MILES_SEATS_TILE}} Miles, **Add Baggage** from {{MILES_BAGS_TILE}} Miles and **Cabin Upgrade** from {{MILES_UPGRADE_TILE}} Miles. Each tile opens a chat with the Defera Concierge, our AI concierge, which sends your request to our team. Here's [how to use Miles for seats or bags](/help/rewards/miles-for-extras).

## How do I grow my balance?

You earn 2 Miles for every $1 of the final amount you pay for a trip, once it's completed. Higher Defera Status tiers earn more. [Earning Defera Miles](/help/rewards/earning-miles) has the details.`,
  },
  {
    slug: 'gift-cards',
    title: 'Redeeming Miles for e-gift cards',
    summary: 'Turn 500 or more Miles into an e-gift card at 200+ merchants, from the Rewards page or in chat. It arrives within 3–5 business days, often sooner.',
    keywords: ['gift card', 'redeem points', 'merchants', 'Amazon gift card', 'Starbucks gift card', 'rewards catalog', 'egift'],
    related: ['rewards/where-is-my-gift-card', 'rewards/miles-value', 'rewards/defera-status', 'contact/how-requests-work'],
    updated: '2026-10-05',
    body: `You can turn your Defera Miles into an e-gift card at more than 200 participating merchants, including Amazon, Starbucks and Target. Tap **Redeem for E-Gift Card** on the **Rewards** page, or ask the Defera Concierge, our AI concierge, in chat.

Every member can do this, whatever your Defera Status tier. You need at least 500 Miles, and our team delivers your e-gift card electronically within 3–5 business days, often sooner.

## How much is my gift card worth?

Each Mile is worth {{MILES_GIFT_CARD_VALUE}} as a gift card. You redeem in steps of 500 Miles, and each step adds $5 of gift-card value.

For example, if you have 1,200 Miles, you can redeem 1,000 of them for a $10 e-gift card. The other 200 Miles stay in your balance.

The Rewards page also has a gift-card value calculator, so you can check before you choose.

## How do I redeem?

1. **Open Rewards.** You'll find it in the main menu.
2. **Tap Redeem for E-Gift Card.** A window opens where you make your choice.
3. **Pick a merchant and an amount.** You can redeem up to your current Miles balance.
4. **Confirm and submit.** You'll be asked to confirm that your choice is final.

> [!IMPORTANT] Your choice is final once you submit it. The merchant and the amount can't be changed afterward, so check both before you submit.

## What happens after I submit?

Here's what happens:

1. **Your Miles are deducted.** They come out of your balance right away.
2. **Your request goes to our team.** It shows as a status card on your **Trips** page and on the Rewards page, marked "Submitted · in progress" while our team works on it.
3. **Our team delivers your e-gift card.** It arrives electronically within 3–5 business days, often sooner. When it's done, the answer appears on your status card, and you get it by email too.

If our team can't fulfill the card, your Miles are returned to your balance. Waiting on one? See [Where’s my e-gift card?](/help/rewards/where-is-my-gift-card)

## Which merchants can I choose?

More than 200, including Starbucks, Amazon, Target, Walmart, Nike, Sephora, DoorDash and Uber. You'll find the full list on the Rewards page.

## Can I ask in chat instead?

Yes. Chat with the [Defera Concierge](/support), our AI concierge, and tell it the merchant and how many Miles you'd like to use. It checks that your balance covers it. If it doesn't, it tells you what you could redeem today.

With your go-ahead, it sends the request to our team. Our team takes the Miles from your balance and delivers your e-gift card within the same 3–5 business days, often sooner.

## Is this the same as the gift cards from Defera Status?

No. Higher Defera Status tiers unlock perks that include free gift cards on early bookings each year. Those are tier benefits. Redeeming your Miles for e-gift cards is open to every member, at any tier.

## Is a gift card the best use of my Miles?

It depends on your plans. The same Miles are worth {{MILES_TRIP_VALUE}} each on a Defera trip, so if another trip is on your mind, they go further at checkout. If you'd like something now, a gift card turns them into value right away.`,
  },
  {
    slug: 'where-is-my-gift-card',
    title: 'Where’s my e-gift card?',
    summary: 'E-gift cards arrive electronically within 3–5 business days, often sooner. You can follow yours on its status card on your Trips page or the Rewards page.',
    keywords: ['gift card not received', 'gift card delay', 'redemption status', 'track gift card', 'gift card email', 'miles deducted'],
    related: ['rewards/gift-cards', 'contact/check-request-status', 'emails/missing-email'],
    updated: '2026-10-23',
    body: `Our team delivers e-gift cards electronically within 3–5 business days of your request, and often sooner. You can follow yours on its status card, on your **Trips** page and on the **Rewards** page.

If it's only been a few days, it's normal not to have it yet. Your request is with our team, and its status card shows where it stands.

## How long does it take?

Delivery takes 3–5 business days, and often less. Weekends don't count, so a request made late in the week may arrive the following week.

For example, if you submit your request on a Friday, expect your e-gift card between Wednesday and Friday of the following week.

## How do I check on it?

1. **Look at your status card.** It's on your **Trips** page and on the Rewards page. It reads "Submitted · in progress" while our team works on your request, then shows the answer once it's resolved.
2. **Check your email.** When our team resolves your request, you also get the answer by email. If you don't see it, look in your spam or junk folder.

## My Miles are gone, but there's no card yet. Is that normal?

Yes. When you redeem on the Rewards page, your Miles come out of your balance right away, and our team then delivers your card within 3–5 business days. If you asked the Defera Concierge in chat instead, our team takes the Miles from your balance when it handles your request.

If our team can't fulfill your e-gift card, your Miles are returned to your balance.

## Can I change the merchant or amount?

No. Your choice is final once you submit it, so the merchant and the amount can't be changed afterward.

## Still waiting after 5 business days?

Chat with the [Defera Concierge](/support), our AI concierge, and tell it:

- the merchant you chose,
- how many Miles you redeemed,
- and the day you submitted your request.

It can send a request to our team to check on it, and you'll get the answer by email.

You can also call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}), where the same AI concierge answers by voice 24/7, or send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'defera-status',
    title: 'Defera Status tiers',
    summary: 'Defera Status has five tiers, from Member to Diamond, based on what you spend on completed trips each calendar year. Higher tiers earn more Miles and perks.',
    keywords: ['tier', 'elite status', 'silver', 'gold', 'platinum', 'diamond', 'member benefits', 'perks', 'qualifying year'],
    related: ['rewards/earning-miles', 'rewards/how-miles-work', 'rewards/gift-cards'],
    updated: '2026-10-23',
    body: `Defera Status is our tier program. The more you spend on completed trips in a calendar year, the higher your tier. Higher tiers earn more Defera Miles and unlock extra perks.

Everyone starts at Member, the default tier. From there, you can move up through Silver, Gold, Platinum and Diamond. You can see your tier and your progress any time on the [Rewards](/rewards) page.

## How is my tier decided?

Your tier is based on the qualifying dollars you spend on completed trips in a calendar year, from January 1 to December 31. We call that year your qualifying year.

A trip counts as completed once it's paid in full and its confirmation number is released. Your confirmation number is the code the airline uses to find your reservation, and our team releases it after your final payment.

## What are the tiers?

From the starting tier up:

1. Member, the default tier
2. Silver
3. Gold
4. Platinum
5. Diamond

## What do higher tiers get?

- **More Miles.** Higher tiers multiply the Miles you earn on completed trips.
- **Perks.** These include preferred seating, checked bags and free gift cards on early bookings each year.

There are also birthday bonuses.

The free gift cards that come with a tier are separate from redeeming your Miles for e-gift cards. Every member can redeem Miles for e-gift cards, at any tier. Here's [how redeeming Miles for e-gift cards works](/help/rewards/gift-cards).

## Where can I see my progress?

Open **Rewards** from the main menu and look for "Your progress". It shows:

- your current tier,
- what you've spent so far in your qualifying year,
- and a projection to the next tier.

## How much do I need to spend for each tier?

The spend each tier needs, the earning multipliers and how each benefit renews are all in the program guide on the Rewards page. They can change over time, so the program guide is the place to check for the current details.

## Questions about your tier?

Chat with the [Defera Concierge](/support), our AI concierge, any time. You can also call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}), where the same AI concierge answers by voice 24/7, or send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'vip',
    title: 'What the VIP badge on your account means',
    summary: 'Our highest-spending customers see a gold VIP badge on their account. It is our way of saying thank you, and it is separate from Defera Status.',
    keywords: ['vip', 'vip status', 'gold badge', 'crown', 'top customers', 'valued customer', 'recognition'],
    related: ['rewards/defera-status', 'rewards/how-miles-work'],
    updated: '2026-10-25',
    body: `A small gold **VIP** badge with a crown appears on the accounts of our highest-spending customers. It's our way of saying thank you for traveling with us.

## Where does the badge show?

If your account has it, you'll see it next to your name on **Profile** and in the account menu, on **Home**, on **Rewards** and on your trip pages. Only you and the Defera team can see it.

## What does VIP include?

VIP is recognition only. It doesn't come with perks or benefits, so it doesn't include upgrades, discounts, fee waivers, extra Miles or exceptions to any policy.

It's also separate from [Defera Status](/help/rewards/defera-status). Your tier, how many Miles you earn and your Status benefits stay exactly the same.

## How do I get it?

Our team gives it automatically to our highest-spending customers. There's no application, no way to request it and no published amount. Once your account has it, it stays for at least 12 months.

## Questions?

Chat with the [Defera Concierge](/support), our AI concierge, any time. You can also call [{{SUPPORT_PHONE}}](tel:{{SUPPORT_PHONE_TEL}}), where the same AI concierge answers by voice 24/7, or send us a message from the [Contact page](/contact#message). Our team replies within 24 hours.`,
  },
  {
    slug: 'miles-when-you-cancel',
    title: 'What happens to Miles when you cancel',
    summary: 'Miles you used on a cancelled trip come back to your account automatically. A cancelled trip doesn’t earn new Miles, however the cancellation is settled.',
    keywords: ['cancelled trip', 'miles back', 'miles refund', 'lose miles', 'points returned', 'cancel booking'],
    related: ['changes-cancellations/cancel-your-trip', 'changes-cancellations/what-you-get-back', 'rewards/book-with-miles', 'rewards/earning-miles'],
    updated: '2026-10-05',
    body: `If you cancel a trip that used Defera Miles, those Miles come back to your account automatically. You don't need to ask for them.

A cancelled trip doesn't earn new Miles, though, because Miles are earned only on completed trips. What happens to any money you paid follows your trip's cancellation terms, and your cancel screen shows those terms.

## What happens to the Miles I used?

It depends on how much of the trip you paid with Miles:

- **Part of the trip:** when the trip is cancelled, your Miles come back to your account automatically. The rest of what you paid follows your trip's cancellation terms. Here's [what you get back when you cancel](/help/changes-cancellations/what-you-get-back).
- **The whole trip:** a trip paid entirely with Miles is cancelled through our team. Open the trip in **Trips**, tap **Flight Options**, then **Cancel**. That opens a chat with the Defera Concierge, our AI concierge, which sends the request to our team. When the trip is cancelled, your Miles come back automatically.

## Does a cancelled trip earn Miles?

No. A cancelled trip earns zero Miles, however the cancellation is settled and whichever Defera plan you were on. Miles are a reward for completed trips, and a cancelled trip never gets there.

If you book another trip later, that trip earns Miles on its own final amount paid, once it's completed.

## What if my booking didn't go through?

Miles you choose at checkout are set aside when you confirm, before your card is charged. If the payment doesn't go through, they're given straight back.

If your bank asked you to approve the payment and that step wasn't finished, your Miles can stay set aside until our team releases them. Chat with the [Defera Concierge](/support), and it can send a request to our team.

## What about e-gift cards?

An e-gift card redemption is final once you submit it, so the merchant and the amount can't be changed. If our team can't fulfill the card, your Miles are returned to your balance.

## Who can help with anything else?

For any other question about Miles on a cancelled trip, chat with the [Defera Concierge](/support). It can send a request to our team, and the answer appears on your trip page and arrives by email.`,
  },
  {
    slug: 'miles-for-extras',
    title: 'Can I use Miles for seats or bags?',
    summary: 'Yes, on a trip that isn’t completed yet. Tap a tile in its “Redeem on this trip” panel for seats, bags or a cabin upgrade, and the Defera Concierge sends your request.',
    keywords: ['seat selection', 'checked bag', 'baggage', 'cabin upgrade', 'redeem on this trip', 'add-ons', 'extras'],
    related: ['seats-and-bags/buy-seats-after-booking', 'seats-and-bags/add-checked-bags', 'seats-and-bags/upgrades', 'rewards/using-miles-at-checkout'],
    updated: '2026-10-05',
    body: `Yes, on a trip that isn't completed yet, such as one you're still paying off. Open the trip and look for the "Redeem on this trip" panel. It has tiles for seats, checked bags and a cabin upgrade.

Each tile starts a request to our team, paid with your Miles. You don't need to work out the details on your own: the Defera Concierge, our AI concierge, writes up the request for you, and our team takes it from there.

## What can I get with Miles?

The panel has three tiles, and each one starts from a set number of Miles:

- **Buy Seats**, from {{MILES_SEATS_TILE}} Miles
- **Add Baggage**, from {{MILES_BAGS_TILE}} Miles
- **Cabin Upgrade**, from {{MILES_UPGRADE_TILE}} Miles

## How does it work?

Here's what happens:

1. **Open your trip.** Go to **Trips** and tap the trip. The "Redeem on this trip" panel is beside or below the Payment Schedule, depending on your screen size.
2. **Tap a tile.** A chat with the Defera Concierge opens, with your request already written out.
3. **Fill in any details.** The concierge may ask a question or two, like your seat preference or which bags you need, and checks with you before sending anything.
4. **Your request goes to our team.** It shows as a card on your trip page, marked "Submitted · in progress" while our team works on it.
5. **You get the answer.** It appears on that card and arrives by email. Our team usually answers within 24 hours.

## Why won't a tile open?

Two things turn a tile off:

- **You don't have enough Miles yet** for that tile.
- **The trip is already completed.** That means it's paid in full and its confirmation number has been released.

Why the second one? The Miles a trip earns go toward your next trip, not the one that earned them. So once a trip is completed, it can't spend Miles, and the panel's heading changes to "Save for future trips".

Your Miles aren't lost. You can use them at checkout on your next trip, where each Mile is worth {{MILES_TRIP_VALUE}}, or turn them into an e-gift card on the **Rewards** page.

## Is a Cabin Upgrade the same as a free upgrade?

No. A **Cabin Upgrade** with Miles is a request you make to our team. It's separate from two other things:

- **Complimentary upgrades,** which our team arranges on some bookings when it can, as a thank-you.
- **The Free Flight Upgrade question** at checkout, which is about moving you to a faster route in the same cabin.

[Upgrades](/help/seats-and-bags/upgrades) explains all three.

## Can I pay by card instead?

Yes. To pay for seats or bags with a card, open the trip's **Flight Options** and tap **Purchase Seats** or **Purchase Baggage**. Here's [how buying seats after you book works](/help/seats-and-bags/buy-seats-after-booking).`,
  },
];
